import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

const ItemMultiSelectModalForItem = ({
  show,
  onClose,
  onAdd,
  items = [],
  title = "Select Items",
  groupKey = "group_name",
  nameKey = "name",
  idKey = "id",
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("name"); // 'name' or 'group'
  const [selectedItems, setSelectedItems] = useState({}); // { [id]: itemData }

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (show && searchInputRef?.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [show]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    if (show) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [show]);

  // Filter items based on search term and selected search criteria
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    
    return items.filter((item) => {
      if (searchBy === "name") {
        const itemName = item[nameKey] ? String(item[nameKey]).toLowerCase() : "";
        const itemId = item[idKey] ? String(item[idKey]).toLowerCase() : "";
        const search = searchTerm.toLowerCase();
        return itemName.includes(search) || itemId.includes(search);
      } else if (searchBy === "group") {
        const groupName = item[groupKey] ? String(item[groupKey]).toLowerCase() : "";
        return groupName.includes(searchTerm.toLowerCase());
      }
      return true;
    });
  }, [items, searchTerm, searchBy, nameKey, idKey, groupKey]);

  const displayedItems = filteredItems.slice(0, 300);

  // Toggle single item selection
  const toggleItem = (item) => {
    const itemId = item[idKey];
    setSelectedItems((prev) => {
      const newSelections = { ...prev };
      if (newSelections[itemId]) {
        delete newSelections[itemId];
      } else {
        newSelections[itemId] = item;
      }
      return newSelections;
    });
  };

  // Toggle "Select All" for currently filtered items
  const isAllFilteredSelected = useMemo(() => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every((item) => {
      const itemId = item[idKey];
      return !!selectedItems[itemId];
    });
  }, [filteredItems, selectedItems, idKey]);

  const toggleSelectAll = () => {
    setSelectedItems((prev) => {
      const newSelections = { ...prev };
      if (isAllFilteredSelected) {
        // Unselect all currently filtered
        filteredItems.forEach((item) => {
          const itemId = item[idKey];
          delete newSelections[itemId];
        });
      } else {
        // Select all currently filtered
        filteredItems.forEach((item) => {
          const itemId = item[idKey];
          newSelections[itemId] = item;
        });
      }
      return newSelections;
    });
  };

  const handleClose = () => {
    setSearchTerm("");
    setSelectedItems({});
    onClose();
  };

  const handleAddRecords = () => {
    if (Object.keys(selectedItems).length > 0) {
      onAdd(Object.values(selectedItems));
      handleClose();
    }
  };

  const selectedCount = Object.keys(selectedItems).length;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center rounded-top">
            <h5 className="modal-title d-flex align-items-center mb-0 font-weight-bold text-dark">
              <i className="fa fa-tasks mr-2 text-primary"></i>
              {t(title)}
              <div className="d-flex align-items-center ml-3">
                <span className="badge font-weight-normal bg-light text-secondary border mr-2 py-1 px-2">
                  {items.length > 0 ? `${filteredItems.length} / ${items.length}` : 0} {t("Results")}
                </span>
                {selectedCount > 0 && (
                  <span className="badge font-weight-normal text-white bg-primary shadow-sm py-1 px-2">
                    {selectedCount} {t("Selected")}
                  </span>
                )}
              </div>
            </h5>
            <button
              type="button"
              className="close xslg-text shadow-none border-0 bg-transparent text-muted"
              onClick={handleClose}
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
          
          <div className="modal-body p-0 bg-light">
            <div className="p-3 border-bottom bg-white">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                <div className="d-flex align-items-center mr-3 mb-2 mb-md-0">
                  <span className="text-muted table-text font-weight-bold mr-3">{t("Search By")}:</span>
                  <div className="d-flex">
                    <div className="form-check custom-radio mb-0 d-flex align-items-center mr-3">
                      <input
                        className="form-check-input cursor-pointer mt-0"
                        type="radio"
                        name="searchBy"
                        id="searchByName"
                        value="name"
                        checked={searchBy === "name"}
                        onChange={(e) => setSearchBy(e.target.value)}
                      />
                      <label className={`form-check-label cursor-pointer ml-2 table-text ${searchBy === 'name' ? 'font-weight-bold text-dark' : 'text-secondary'}`} htmlFor="searchByName">
                        {t("Item Name / ID")}
                      </label>
                    </div>
                    <div className="form-check custom-radio mb-0 d-flex align-items-center">
                      <input
                        className="form-check-input cursor-pointer mt-0"
                        type="radio"
                        name="searchBy"
                        id="searchByGroup"
                        value="group"
                        checked={searchBy === "group"}
                        onChange={(e) => setSearchBy(e.target.value)}
                      />
                      <label className={`form-check-label cursor-pointer ml-2 table-text ${searchBy === 'group' ? 'font-weight-bold text-dark' : 'text-secondary'}`} htmlFor="searchByGroup">
                        {t("Group Name")}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="position-relative flex-grow-1 flex-md-grow-0" style={{ minWidth: '280px' }}>
                  <i className="fa fa-search position-absolute text-secondary" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }}></i>
                  <input
                    type="text"
                    className="form-control form-control-sm pl-5 py-2 shadow-sm border-0 bg-light table-text rounded"
                    placeholder={searchBy === 'name' ? t("Search by item name or ID...") : t("Search by group name...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    ref={searchInputRef}
                  />
                </div>
              </div>
            </div>

            <div className="custom-scrollbar px-2 pt-2 pb-3" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              <div className="bg-white rounded border overflow-hidden">
                <table className="table table-hover table-sm mb-0">
                  <thead className="bg-light sticky-top shadow-sm" style={{ zIndex: 1 }}>
                    <tr>
                      <th className="align-middle border-bottom-0" style={{ width: '70px' }}>
                        <div className="form-check cursor-pointer mt-0 custom-checkbox mb-0 d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isAllFilteredSelected}
                            onChange={toggleSelectAll}
                          />
                        </div>
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary" style={{ minWidth: '80px', letterSpacing: '0.5px' }}>
                        {t("ID")}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary" style={{ letterSpacing: '0.5px' }}>
                        {t("Name")}
                      </th>
                      <th className="text-uppercase font-weight-bold border-bottom-0 new-sm-text text-secondary" style={{ minWidth: '150px', letterSpacing: '0.5px' }}>
                        {t("Group Name")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedItems.length > 0 ? (
                      displayedItems.map((item) => {
                        const itemId = item[idKey];
                        const isSelected = !!selectedItems[itemId];
                        return (
                          <tr
                            key={itemId}
                            onClick={() => toggleItem(item)}
                            style={{
                              backgroundColor: isSelected ? 'rgba(0, 123, 255, 0.04)' : 'transparent'
                            }}
                            className="cursor-pointer"
                          >
                            <td className="align-middle">
                              <div className="custom-checkbox mb-0 d-flex justify-content-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer mt-0"
                                  checked={isSelected}
                                  readOnly
                                />
                              </div>
                            </td>
                            <td className={`py-2 align-middle table-text ${isSelected ? 'text-primary font-weight-bold' : 'text-secondary'}`}>
                              #{itemId}
                            </td>
                            <td className={`py-2 align-middle table-text ${isSelected ? 'text-dark font-weight-bold' : 'text-dark'}`}>
                              {item[nameKey] || "-"}
                            </td>
                            <td className="py-2 align-middle table-text text-secondary">
                              <span className="badge bg-light text-secondary border font-weight-normal px-2 py-1">
                                {item[groupKey] || "-"}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center py-5 text-muted border-0">
                          <div className="d-flex flex-column align-items-center">
                            <i className="fa fa-folder-open-o mb-3 text-secondary" style={{ fontSize: '2.5rem' }}></i>
                            <span className="table-text font-weight-bold">{t("No Item Found")}</span>
                            {searchTerm && (
                              <small className="mt-1 text-secondary">{t("Try adjusting your search criteria")}</small>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer py-3 px-4 bg-white d-flex justify-content-between align-items-center border-top rounded-bottom">
            <small className="text-muted d-flex align-items-center sm-text">
              <kbd className="mr-2 shadow-sm bg-light text-secondary border font-weight-bold">ESC</kbd> {t("to close")}
            </small>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary px-4 py-2 table-text font-weight-bold rounded"
                onClick={handleClose}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary px-4 py-2 d-flex align-items-center table-text font-weight-bold rounded"
                disabled={selectedCount === 0}
                onClick={handleAddRecords}
              >
                <i className="fa fa-plus mr-2"></i>
                {t("Add Selected Items")}
                {selectedCount > 0 && <span className="badge bg-white text-primary ml-2">{selectedCount}</span>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemMultiSelectModalForItem;
